/**
 * Consultation page analytics (FIF-117)
 * ----------------------------------------------------------------------------
 * Instruments the booking funnel for the consultation product page so we can
 * measure lift from the redesign (3-cards + dropdown) against the previous
 * single-button layout.
 *
 * Five events are emitted to BOTH GA4 (gtag / dataLayer fallback) and Klaviyo
 * (klaviyo.track / _learnq fallback):
 *
 *   1. consultation_page_view        — fires once on init
 *   2. consultation_variant_selected — fires when the dropdown variant changes
 *   3. consultation_card_cta_clicked — fires on .js-consultation-package-button click
 *   4. consultation_calendar_opened  — fires when the Servicify modal mounts
 *   5. consultation_booking_completed— exposed via window.ConsultationAnalytics
 *      so the success page or app webhook can call it post-booking
 *
 * Suppression: events are no-ops in the theme editor (Shopify.designMode) and
 * on non-production stores (window.consultationAnalyticsConfig.isProduction).
 *
 * The module is self-contained, idempotent, and side-effect-free outside the
 * consultation template (it only attaches DOM listeners after init runs).
 */
(() => {
  if (window.__ffConsultationAnalyticsInitialized) return;
  window.__ffConsultationAnalyticsInitialized = true;

  const EVENT = Object.freeze({
    PAGE_VIEW: 'consultation_page_view',
    VARIANT_SELECTED: 'consultation_variant_selected',
    CARD_CTA_CLICKED: 'consultation_card_cta_clicked',
    CALENDAR_OPENED: 'consultation_calendar_opened',
    BOOKING_COMPLETED: 'consultation_booking_completed',
  });

  const SELECTOR = Object.freeze({
    PRODUCT_BANNER: 'ff-product-banner',
    VARIANT_INPUT: 'input[name="id"]',
    PACKAGE_BUTTON: '.js-consultation-package-button',
    SERVICIFY_OVERLAY: '.ReactModalPortal .ReactModal__Overlay',
  });

  const SESSION_STORAGE_KEY = 'ff_consultation_booking_session';
  const VARIANT_INIT_DELAY_MS = 100;
  const SOURCE = 'consultation_page';

  const config = window.consultationAnalyticsConfig || {};

  // ---------- Suppression -----------------------------------------------------

  function isSuppressed() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return true;
    if (config.isProduction !== true) return true;
    return false;
  }

  // ---------- Transport -------------------------------------------------------

  function trackGA4(eventName, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
        return;
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, params));
      }
    } catch (_) {
      /* non-fatal: analytics transport unavailable */
    }
  }

  function trackKlaviyo(eventName, params) {
    try {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(eventName, params);
        return;
      }
      if (Array.isArray(window._learnq)) {
        window._learnq.push(['track', eventName, params]);
      }
    } catch (_) {
      /* non-fatal: analytics transport unavailable */
    }
  }

  function track(eventName, params) {
    if (isSuppressed()) return;
    const enriched = Object.assign(
      {
        source: SOURCE,
        page_url: window.location.pathname + window.location.search,
        product_id: config.productId || null,
        product_handle: config.productHandle || null,
      },
      params || {}
    );
    trackGA4(eventName, enriched);
    trackKlaviyo(eventName, enriched);
  }

  // ---------- Session helpers (carry context across events) -------------------

  function persistBookingIntent(detail) {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify(Object.assign({ ts: Date.now() }, detail))
      );
    } catch (_) {
      /* sessionStorage may be unavailable (private mode) — non-fatal */
    }
  }

  function readBookingIntent() {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function clearBookingIntent() {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {
      /* non-fatal */
    }
  }

  // ---------- Event 1: page view ---------------------------------------------

  function emitPageView() {
    track(EVENT.PAGE_VIEW, {});
  }

  // ---------- Event 2: variant selected --------------------------------------

  let lastVariantId = null;

  function readSelectedVariant(banner) {
    const input = banner && banner.querySelector(SELECTOR.VARIANT_INPUT);
    if (!input || !input.value) return null;
    const select = banner.querySelector('select');
    const option = select ? select.options[select.selectedIndex] : null;
    return {
      variant_id: input.value,
      variant_name: option ? option.text : '',
    };
  }

  function emitVariantChange(banner) {
    const snapshot = readSelectedVariant(banner);
    if (!snapshot || !snapshot.variant_id) return;
    if (snapshot.variant_id === lastVariantId) return;
    lastVariantId = snapshot.variant_id;
    track(EVENT.VARIANT_SELECTED, snapshot);
  }

  function setupVariantTracking() {
    const banner = document.querySelector(SELECTOR.PRODUCT_BANNER);
    if (!banner) return;
    banner.addEventListener('change', () => emitVariantChange(banner));
    setTimeout(() => emitVariantChange(banner), VARIANT_INIT_DELAY_MS);
  }

  // ---------- Event 3: card CTA clicked --------------------------------------

  function buildCtaPayload(button) {
    return {
      tier: button.dataset.tier || (button.dataset.packageName || '').toLowerCase(),
      variant_id: button.dataset.variantId || null,
      price: button.dataset.price || null,
      package_name: button.dataset.packageName || null,
    };
  }

  function setupCardClickTracking() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest(SELECTOR.PACKAGE_BUTTON);
      if (!button) return;
      const payload = buildCtaPayload(button);
      track(EVENT.CARD_CTA_CLICKED, payload);
      persistBookingIntent(payload);
    });
  }

  // ---------- Event 4: calendar opened ---------------------------------------

  let calendarOpenObserved = false;

  function emitCalendarOpened() {
    const intent = readBookingIntent() || {};
    track(EVENT.CALENDAR_OPENED, {
      tier: intent.tier || null,
      variant_id: intent.variant_id || lastVariantId || null,
    });
  }

  function setupCalendarObserver() {
    const observer = new MutationObserver(() => {
      const overlayPresent = !!document.querySelector(SELECTOR.SERVICIFY_OVERLAY);
      if (overlayPresent && !calendarOpenObserved) {
        calendarOpenObserved = true;
        emitCalendarOpened();
      } else if (!overlayPresent && calendarOpenObserved) {
        // Modal closed — re-arm so a re-open fires the event again.
        calendarOpenObserved = false;
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------- Event 5: booking completed (public API) ------------------------

  // Booking confirmation lives inside the Easy Appointment Booking app's
  // iframe / portal and does not expose a documented JS callback. We therefore
  // expose a public API so:
  //   (a) a `?consultation_booked=1` URL param check on the next pageview can
  //       fire it, or
  //   (b) the Shopify thank-you / order-status page can `<script>`-call it.
  // Detail keys are optional; missing values fall back to the persisted
  // booking-intent payload from the original card click.
  function trackBookingCompleted(detail) {
    const intent = readBookingIntent() || {};
    const payload = Object.assign({}, detail || {});
    track(EVENT.BOOKING_COMPLETED, {
      order_id: payload.order_id || null,
      tier: payload.tier || intent.tier || null,
      variant_id: payload.variant_id || intent.variant_id || null,
      value: payload.value || intent.price || null,
    });
    clearBookingIntent();
  }

  window.ConsultationAnalytics = {
    track,
    trackBookingCompleted,
    EVENT,
  };

  // ---------- Init ------------------------------------------------------------

  function init() {
    if (isSuppressed()) return;
    emitPageView();
    setupVariantTracking();
    setupCardClickTracking();
    setupCalendarObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
